import React, { useState, useEffect, useCallback } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getDevotionalForDay, updateDevotional } from '../../services/devotionalService';
import runMigration from '../../utils/runMigration';
import SubscriptionErrorMonitor from './SubscriptionErrorMonitor';
import RestoreSubscribers from '../RestoreSubscribers';
import { collection, query, orderBy, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadMultipleAudioFiles, getAudioFileUrl } from '../../utils/audioUploadService';

const calculateDayOfYear = (date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const diffTime = date - startOfYear;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1;
};

const getDateFromDayOfYear = (dayOfYear) => {
  const currentYear = new Date().getFullYear();
  const date = new Date(currentYear, 0, 1);
  date.setDate(date.getDate() + dayOfYear - 1);
  return date;
};

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

const AdminPanel = () => {
  const today = new Date();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [currentDay, setCurrentDay] = useState(calculateDayOfYear(today));
  const [selectedDate, setSelectedDate] = useState(formatDateForInput(today));
  const [devotional, setDevotional] = useState(null);
  const [message, setMessage] = useState('');
  const [isMigrating, setIsMigrating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('devotionals'); // New state for tab management
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const auth = getAuth();

  const loadDevotional = useCallback(async (day) => {
    setIsLoading(true);
    setMessage('');
    try {
      const data = await getDevotionalForDay(day);
      if (data) {
        setDevotional(data);
      } else {
        setMessage(`No devotional found for day ${day}`);
        setDevotional(null);
      }
    } catch (error) {
      console.error('Error loading devotional:', error);
      setMessage('Error loading devotional: ' + error.message);
      setDevotional(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      if (user) {
        loadDevotional(currentDay);
      }
    });

    return () => unsubscribe();
  }, [auth, currentDay, loadDevotional]);

  useEffect(() => {
    if (isAuthenticated) {
      loadDevotional(currentDay);
    }
  }, [currentDay, isAuthenticated, loadDevotional]);

  const handleDateChange = (e) => {
    const date = new Date(e.target.value);
    setSelectedDate(e.target.value);
    const dayOfYear = calculateDayOfYear(date);
    setCurrentDay(dayOfYear);
    loadDevotional(dayOfYear);
  };

  const handleDayChange = (e) => {
    const day = parseInt(e.target.value);
    if (day >= 1 && day <= 366) {
      setCurrentDay(day);
      const date = getDateFromDayOfYear(day);
      setSelectedDate(formatDateForInput(date));
      loadDevotional(day);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setMessage('Successfully logged in!');
    } catch (error) {
      setMessage('Login error: ' + error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setMessage('Logged out successfully');
      setDevotional(null);
    } catch (error) {
      setMessage('Logout error: ' + error.message);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      await updateDevotional(currentDay, {
        chapter: parseInt(devotional.chapter),
        verse: parseInt(devotional.verse),
        scriptureReference: devotional.scriptureReference,
        verseText: devotional.verseText,
        title: devotional.title,
        devotionalContent: devotional.devotionalContent,
        prayer: devotional.prayer,
        dailyChallenge: devotional.dailyChallenge,
        translation: devotional.translation || 'NET',
        author: devotional.author || 'admin_update',
        version: (devotional.version || 0) + 1
      });

      // Reload the devotional to show updated content
      await loadDevotional(currentDay);
      setMessage('Devotional updated successfully! Please refresh the live site to see changes.');
    } catch (error) {
      setMessage('Update error: ' + error.message);
    }
  };

  const handleMigration = async () => {
    if (window.confirm('Are you sure you want to migrate the CSV data to Firestore? This should only be done once.')) {
      setIsMigrating(true);
      setMessage('Starting migration...');
      try {
        const result = await runMigration();
        if (result.success) {
          setMessage(`Migration successful! ${result.count} devotionals migrated.`);
        } else {
          setMessage('Migration failed: ' + result.error);
        }
      } catch (error) {
        setMessage('Migration failed: ' + error.message);
      } finally {
        setIsMigrating(false);
      }
    }
  };

  // Load contact submissions
  const loadContactSubmissions = async () => {
    setLoadingContacts(true);
    try {
      const q = query(
        collection(db, 'contact_submissions'),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const submissions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
      setContactSubmissions(submissions);
    } catch (error) {
      console.error('Error loading contact submissions:', error);
    }
    setLoadingContacts(false);
  };

  // Mark submission as read/unread
  const toggleSubmissionStatus = async (submissionId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'new' ? 'read' : 'new';
      await updateDoc(doc(db, 'contact_submissions', submissionId), {
        status: newStatus
      });
      // Update local state
      setContactSubmissions(prev =>
        prev.map(sub =>
          sub.id === submissionId
            ? { ...sub, status: newStatus }
            : sub
        )
      );
    } catch (error) {
      console.error('Error updating submission status:', error);
    }
  };

  // Load contact submissions when tab changes
  useEffect(() => {
    if (activeTab === 'contacts') {
      loadContactSubmissions();
    }
  }, [activeTab]);

  const handleAudioUpload = async (event) => {
    event.preventDefault();

    const files = event.target.files;
    if (!files || files.length === 0) {
      console.log('No files selected');
      return;
    }

    // Log the selected files
    console.log('Files selected:', {
      count: files.length,
      names: Array.from(files).map(f => f.name),
      sizes: Array.from(files).map(f => f.size),
      types: Array.from(files).map(f => f.type)
    });

    // Validate file names
    const invalidFiles = Array.from(files).filter(file => {
      const fileNamePattern = /^\d{4}-\d{2}-\d{2}\.mp3$/;
      return !fileNamePattern.test(file.name);
    });

    if (invalidFiles.length > 0) {
      const errorMsg = `Some files have invalid names. Please use the format YYYY-MM-DD.mp3:\n${invalidFiles.map(f => f.name).join('\n')}`;
      console.error(errorMsg);
      setUploadError(errorMsg);
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      // Verify authentication
      if (!auth.currentUser) {
        throw new Error('You must be logged in to upload files');
      }

      console.log('Starting upload with authenticated user:', auth.currentUser.email);

      // Convert FileList to Array and upload
      const fileArray = Array.from(files);
      const results = await uploadMultipleAudioFiles(fileArray);

      console.log('Upload completed successfully:', results);

      const successCount = results.length;
      setUploadSuccess(`Successfully uploaded ${successCount} audio file${successCount !== 1 ? 's' : ''}`);

      // Clear the file input
      event.target.value = '';
    } catch (error) {
      console.error('Upload failed:', {
        error: error.message,
        stack: error.stack
      });
      setUploadError(error.message || 'Error uploading audio files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Admin Login</h2>
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <input
              type="email"
              placeholder="Email"
              className="w-full p-2 border rounded"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <input
              type="password"
              placeholder="Password"
              className="w-full p-2 border rounded"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          >
            Login
          </button>
        </form>
        {message && <p className="mt-4 text-red-500">{message}</p>}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <button
          onClick={handleLogout}
          className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
        >
          Logout
        </button>
      </div>

      {/* Tab Navigation - Updated to match current UI */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('devotionals')}
            className={`px-4 py-2 ${activeTab === 'devotionals'
              ? 'text-blue-600 border-b-2 border-blue-500'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Devotionals
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`px-4 py-2 ${activeTab === 'subscribers'
              ? 'text-blue-600 border-b-2 border-blue-500'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Subscribers
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2 ${activeTab === 'contacts'
              ? 'text-blue-600 border-b-2 border-blue-500'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Contact Messages
          </button>
          <button
            onClick={() => setActiveTab('errors')}
            className={`px-4 py-2 ${activeTab === 'errors'
              ? 'text-blue-600 border-b-2 border-blue-500'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Subscription Errors
          </button>
        </nav>
      </div>

      {/* Migrate CSV Button - Only show in Devotionals tab */}
      {activeTab === 'devotionals' && (
        <div className="flex justify-end mb-4">
          <button
            onClick={handleMigration}
            disabled={isMigrating}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
          >
            {isMigrating ? 'Migrating...' : 'Migrate CSV to Firestore'}
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'devotionals' && (
        <>
          {/* Date Selection Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h2 className="text-xl font-bold mb-4">Select Devotional Date</h2>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label className="block mb-2">Select Date:</label>
                <div className="relative">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    min="2025-01-01"
                    max="2026-12-31"
                    className="w-full p-2 border rounded appearance-none bg-white"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    Selected: {formatDateForDisplay(selectedDate)}
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <label className="block mb-2">Or Enter Day of Year (1-366):</label>
                <input
                  type="number"
                  min="1"
                  max="366"
                  value={currentDay}
                  onChange={handleDayChange}
                  className="w-24 p-2 border rounded"
                />
              </div>
              {isLoading && <span className="ml-3 text-gray-600">Loading...</span>}
            </div>
          </div>

          {/* Audio Upload Section */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Audio Upload</h3>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
              <input
                type="file"
                accept="audio/mpeg"
                multiple
                onChange={handleAudioUpload}
                disabled={isUploading}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
              {isUploading && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Uploading...</span>
                </div>
              )}
              {uploadError && (
                <div className="text-red-600 bg-red-50 p-3 rounded">
                  <p className="font-medium">Upload Error</p>
                  <p className="text-sm whitespace-pre-wrap">{uploadError}</p>
                </div>
              )}
              {uploadSuccess && (
                <div className="text-green-600 bg-green-50 p-3 rounded">
                  <p>{uploadSuccess}</p>
                </div>
              )}
            </form>
          </div>

          {/* Devotional Form - Only show when a devotional is loaded */}
          {devotional && (
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4">Edit Devotional</h2>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-2 font-medium">Chapter:</label>
                    <input
                      type="number"
                      value={devotional.chapter}
                      onChange={(e) => setDevotional({ ...devotional, chapter: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium">Verse:</label>
                    <input
                      type="number"
                      value={devotional.verse}
                      onChange={(e) => setDevotional({ ...devotional, verse: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block mb-2 font-medium">Scripture Reference:</label>
                  <input
                    type="text"
                    value={devotional.scriptureReference}
                    onChange={(e) => setDevotional({ ...devotional, scriptureReference: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 font-medium">Verse Text:</label>
                  <textarea
                    value={devotional.verseText}
                    onChange={(e) => setDevotional({ ...devotional, verseText: e.target.value })}
                    className="w-full p-2 border rounded h-24"
                    rows={3}
                  />
                </div>

                <div className="mb-4">
                  <label className="block mb-2 font-medium">Translation:</label>
                  <select
                    value={devotional.translation || 'NET'}
                    onChange={(e) => setDevotional({ ...devotional, translation: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="NET">NET (Default)</option>
                    <option value="NIV">NIV (New International Version)</option>
                    <option value="NLT">NLT (New Living Translation)</option>
                    <option value="MSG">MSG (The Message)</option>
                    <option value="TPT">TPT (The Passion Translation)</option>
                    <option value="ESV">ESV (English Standard Version)</option>
                    <option value="NASB">NASB (New American Standard Bible)</option>
                    <option value="KJV">KJV (King James Version)</option>
                    <option value="NKJV">NKJV (New King James Version)</option>
                    <option value="YLT">YLT (Young's Literal Translation)</option>
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Select the translation used for the verse text above
                  </p>
                </div>

                <div>
                  <label className="block mb-2 font-medium">Title:</label>
                  <input
                    type="text"
                    value={devotional.title}
                    onChange={(e) => setDevotional({ ...devotional, title: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block mb-2 font-medium">Devotional Content:</label>
                  <div className="mb-2 text-sm text-gray-600">
                    Formatting: Use **text** for bold, *text* for italic, and _text_ for underline
                  </div>
                  <textarea
                    value={devotional.devotionalContent}
                    onChange={(e) => setDevotional({ ...devotional, devotionalContent: e.target.value })}
                    className="w-full p-2 border rounded h-40"
                    rows={6}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-medium">Prayer:</label>
                  <textarea
                    value={devotional.prayer}
                    onChange={(e) => setDevotional({ ...devotional, prayer: e.target.value })}
                    className="w-full p-2 border rounded h-32"
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block mb-2 font-medium">Daily Challenge:</label>
                  <textarea
                    value={devotional.dailyChallenge}
                    onChange={(e) => setDevotional({ ...devotional, dailyChallenge: e.target.value })}
                    className="w-full p-2 border rounded h-32"
                    rows={4}
                  />
                </div>

                <button
                  type="submit"
                  className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600"
                >
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {!devotional && !isLoading && (
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <p className="text-gray-600">Select a date or day to load a devotional</p>
            </div>
          )}
        </>
      )}

      {activeTab === 'subscribers' && (
        <div>
          <h2 className="text-xl font-bold mb-4">Subscriber Management</h2>
          <RestoreSubscribers />
        </div>
      )}

      {activeTab === 'contacts' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Contact Form Submissions</h2>
            <button
              onClick={loadContactSubmissions}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Refresh
            </button>
          </div>

          {loadingContacts ? (
            <div className="text-center py-4">Loading submissions...</div>
          ) : contactSubmissions.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No submissions yet</div>
          ) : (
            <div className="grid gap-4">
              {contactSubmissions.map(submission => (
                <div
                  key={submission.id}
                  className={`bg-white rounded-lg shadow-sm p-4 border-l-4 ${submission.status === 'new' ? 'border-blue-500' : 'border-gray-300'
                    }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">{submission.subject}</h3>
                      <p className="text-sm text-gray-500">
                        From: {submission.name} ({submission.email})
                      </p>
                    </div>
                    <button
                      onClick={() => toggleSubmissionStatus(submission.id, submission.status)}
                      className={`px-3 py-1 rounded text-sm ${submission.status === 'new'
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {submission.status === 'new' ? 'Mark as Read' : 'Mark as Unread'}
                    </button>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{submission.message}</p>
                  <div className="mt-2 text-sm text-gray-500">
                    {submission.timestamp ? new Date(submission.timestamp).toLocaleString() : 'No timestamp'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'errors' && (
        <div>
          <h2 className="text-xl font-bold mb-4">Subscription Errors</h2>
          <SubscriptionErrorMonitor />
        </div>
      )}

      {message && (
        <div className="mt-4 p-3 bg-blue-100 text-blue-700 rounded">
          {message}
        </div>
      )}
    </div>
  );
};

export default AdminPanel; 